import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getNCRById,
  deleteNCR,
  markInProgress,
  markCorrected,
  closeNCR,
  uploadNCRAttachment
} from "../../../services/api/qa/ncrService";

import NCRAssignModule from "./components/NCRAssignModule";

export default function ViewNCR() {

  const { id } = useParams();
  const navigate = useNavigate();

  const [ncr, setNcr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));

  const canEdit = user?.role === "admin" || user?.role === "qa_manager";

  const loadNCR = async () => {
    try {
      setLoading(true);
      const res = await getNCRById(id);
      setNcr(res.data);
    } catch {
      toast.error("Failed to load NCR");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNCR();
  }, [id]);

  /* ---------------- DELETE ---------------- */

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await deleteNCR(id);
      toast.success("NCR deleted");
      navigate("/ncr");
    } catch {
      toast.error("Delete failed");
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  /* ---------------- STATUS ---------------- */

  const updateStatus = async (type) => {
    try {

      setActionLoading(true);

      if (type === "in_progress") await markInProgress(id);
      if (type === "corrected") await markCorrected(id);
      if (type === "closed") await closeNCR(id);

      toast.success("Status updated");

      loadNCR();

    } catch {
      toast.error("Status update failed");
    } finally {
      setActionLoading(false);
    }
  };

  /* ---------------- FILE UPLOAD ---------------- */

  const handleFileUpload = async (e) => {

    const file = e.target.files[0];
    if (!file) return;

    try {

      await uploadNCRAttachment(id, file);

      toast.success("Attachment uploaded");

      loadNCR();

    } catch {

      toast.error("Upload failed");

    }
  };

  /* ---------------- STATUS BADGE ---------------- */

  const statusBadge = (status) => {

    const styles = {
      open: "bg-red-100 text-red-700",
      in_progress: "bg-yellow-100 text-yellow-700",
      corrected: "bg-blue-100 text-blue-700",
      closed: "bg-green-100 text-green-700"
    };

    return (
      <span
        className={`px-3 py-1 rounded text-sm font-medium ${styles[status]}`}
      >
        {status?.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  if (loading) return <p className="p-6">Loading...</p>;
  if (!ncr) return <p className="p-6 text-red-500">NCR not found</p>;

  return (

    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">

      {/* HEADER */}

      <div className="flex flex-col md:flex-row justify-between gap-4">

        <div>
          <h2 className="text-2xl font-bold">{ncr.title}</h2>
          {statusBadge(ncr.status)}
        </div>

        <div className="flex gap-2 flex-wrap">

          {canEdit && (
            <Link
              to={`/ncr/edit/${id}`}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Edit
            </Link>
          )}

          {canEdit && (
            <button
              onClick={() => setConfirmAction(() => handleDelete)}
              className="bg-red-600 text-white px-4 py-2 rounded"
            >
              Delete
            </button>
          )}

        </div>
      </div>

      {/* PROJECT DETAILS */}

      <div className="bg-white shadow rounded p-6">

        <h3 className="font-semibold text-lg mb-4">
          Project Details
        </h3>

        <div className="grid md:grid-cols-2 gap-4 text-sm">

          <p><strong>Project:</strong> {ncr.project?.project_name}</p>

          <p><strong>Project Code:</strong> {ncr.project?.project_code}</p>

          <p><strong>Project Type:</strong> {ncr.project?.project_type}</p>

          <p><strong>Status:</strong> {ncr.project?.status}</p>

          <p><strong>Start Date:</strong> {ncr.project?.start_date}</p>

          <p><strong>End Date:</strong> {ncr.project?.end_date}</p>

        </div>

      </div>

      {/* BOQ DETAILS */}

      <div className="bg-white shadow rounded p-6">

        <h3 className="font-semibold text-lg mb-4">
          BOQ Item
        </h3>

        <div className="grid md:grid-cols-2 gap-4 text-sm">

          <p><strong>Item:</strong> {ncr.boq_item?.item_name}</p>

          <p><strong>Unit:</strong> {ncr.boq_item?.unit}</p>

          <p><strong>Quantity:</strong> {ncr.boq_item?.quantity}</p>

          <p><strong>Rate:</strong> ₹ {ncr.boq_item?.rate}</p>

          <p><strong>Total:</strong> ₹ {ncr.boq_item?.total_amount}</p>

          <p><strong>Approved Make:</strong> {ncr.boq_item?.approved_make}</p>

          <p><strong>Offered Make:</strong> {ncr.boq_item?.offered_make}</p>

        </div>

      </div>

      {/* NCR DETAILS */}

      <div className="bg-white shadow rounded p-6">

        <h3 className="font-semibold text-lg mb-4">
          NCR Details
        </h3>

        <div className="grid md:grid-cols-2 gap-4 text-sm">

          <p><strong>Severity:</strong> {ncr.severity}</p>

          <p>
            <strong>Assigned To:</strong>{" "}
            {ncr.assigned_user?.name || "Not Assigned"}
          </p>

          <p><strong>Due Date:</strong> {ncr.due_date || "Not Set"}</p>

        </div>

        {/* ASSIGN MODULE */}

        <div className="mt-6">

          <NCRAssignModule
            ncrId={id}
            assignedTo={ncr.assigned_to}
            refresh={loadNCR}
          />

        </div>

        {/* STATUS ACTIONS */}

        <div className="mt-6 flex flex-wrap gap-3">

          <button
            onClick={() => updateStatus("in_progress")}
            disabled={actionLoading}
            className="bg-yellow-500 text-white px-4 py-2 rounded"
          >
            Mark In Progress
          </button>

          <button
            onClick={() => updateStatus("corrected")}
            disabled={actionLoading}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Mark Corrected
          </button>

          <button
            onClick={() => updateStatus("closed")}
            disabled={actionLoading}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Close NCR
          </button>

        </div>

      </div>

      {/* ISSUE DESCRIPTION */}

      <div className="bg-white shadow rounded p-6">

        <h3 className="font-semibold mb-2">
          Issue Description
        </h3>

        <p className="text-sm">
          {ncr.issue_description}
        </p>

      </div>

      {/* ATTACHMENTS */}

      <div className="bg-white shadow rounded p-6">

        <h3 className="font-semibold mb-3">
          Attachments
        </h3>

        <input
          type="file"
          onChange={handleFileUpload}
          className="border p-2 rounded"
        />

        <ul className="list-disc ml-5 mt-4 text-sm space-y-1">

          {ncr.attachments?.map((file, i) => (
            <li key={i}>
              <a
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                {file.name}
              </a>
            </li>
          ))}

        </ul>

      </div>

      {/* DELETE CONFIRM */}

      {confirmAction && (

        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">

          <div className="bg-white p-6 rounded w-72">

            <p>Are you sure you want to delete this NCR?</p>

            <div className="flex justify-end gap-2 mt-4">

              <button
                onClick={() => setConfirmAction(null)}
                className="border px-4 py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={confirmAction}
                className="bg-red-600 text-white px-4 py-2 rounded"
              >
                Delete
              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );
}